import React, { forwardRef, ReactElement, Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField, { TextFieldProps } from "@material-ui/core/TextField/TextField";
import { v4 as uuid } from "uuid";
import { tesaRed } from "../../styles/colors";
import useDebounce from "../../hooks/useDebounce.hook";
import PagedResponse from "../../api/models/PagedResponse";
import { match, parse } from "../../lib/AutosuggestHighlight";
import OrderedRequestHelper from "../../helper/OrderedRequestHelper";

export interface AutocompleteSelectable<T> {
  key: string;
  label: string;
  data: T | null;
}

export interface CustomSearchAutocompleteProps<T> {
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  excludedOptions?: T[];
  freeSolo?: boolean;
  freeze?: boolean;
  helperText?: string;
  initialData?: T[];
  inputLabel: string;
  mandatory?: boolean;
  open?: boolean;
  openOnFocus?: boolean;
  value?: T | null;
  map: (data: T) => { key: string; label: string };
  mapToData?: (inputValue: string) => T;
  onChange: (event: React.ChangeEvent<unknown>, value: T | null) => void;
  onInputChange?: (event: React.ChangeEvent<unknown>, value: string) => void;
  optionFetch: (term: string) => Promise<PagedResponse<T>>;
}

const useStyles = makeStyles({
  error: {
    color: `${tesaRed} !important`,
  },
});

function CustomSearchAutocomplete<T>(
  props: MergedProps<CustomSearchAutocompleteProps<T>, TextFieldProps>,
  ref: Ref<{ reset: (preserveInputValue?: boolean) => void }> | undefined,
): ReactElement {
  const classes = useStyles(props);
  const {
    autoFocus,
    disabled,
    error,
    freeSolo,
    excludedOptions,
    helperText,
    initialData,
    inputLabel,
    mandatory,
    open,
    openOnFocus,
    map,
    mapToData,
    onChange,
    optionFetch,
    value,
    ...TextFieldProps
  } = props;

  const mapDataValue = (data: T): AutocompleteSelectable<T> => {
    const { key, label } = map(data);

    return {
      key,
      label,
      data,
    };
  };

  const initOptions = (data?: T[]): AutocompleteSelectable<T>[] => data?.map((elm) => mapDataValue(elm)) || [];

  const [term, setTerm] = useState(value ? mapDataValue(value).label : "");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(Boolean(initialData));
  const [options, setOptions] = useState<AutocompleteSelectable<T>[]>(initOptions(initialData));
  const [freeze, setFreeze] = useState(false);
  const [orderedRequestHelper] = useState(new OrderedRequestHelper());
  const fetchInProgress = useRef(false);

  const debouncedTerm = useDebounce(term, 300);
  const excludedValues = excludedOptions?.map(mapDataValue);

  const freezedFilter = (): void => {
    setLoading(false);
    setOptions(
      options.filter((o) =>
        debouncedTerm
          .split(" ")
          .filter((s) => !!s)
          .map((s) => s.toLowerCase())
          .map((s) => o.label.toLowerCase().includes(s))
          .reduce((a, b) => a && b, true),
      ),
    );
  };

  const fetch = (): void => {
    fetchInProgress.current = true;
    setLoading(true);
    orderedRequestHelper
      .ordered(() => optionFetch(debouncedTerm))
      .then((results) => {
        fetchInProgress.current = false;
        setLoading(false);
        setOptions(results.data.map(mapDataValue));

        if (freeze) {
          setFreeze(results.totalPages === 1);
        }
      })
      .catch(() => {
        fetchInProgress.current = false;
        setLoading(false);
        setOptions([]);
        setFreeze(false);
      });
  };

  const filterOptions = excludedValues?.length
    ? (selectables: AutocompleteSelectable<T>[]): AutocompleteSelectable<T>[] =>
        selectables.filter((selectable) => !excludedValues.some((val) => val.key === selectable.key))
    : (x: AutocompleteSelectable<T>[]): AutocompleteSelectable<T>[] => x;

  useImperativeHandle(ref, () => ({
    reset: (preserveInputValue?: boolean): void => {
      if (!preserveInputValue) {
        setTerm("");
      }
      setFreeze(false);
      fetch();
    },
  }));

  useEffect(() => {
    if (freeze) {
      freezedFilter();
    } else if (!loaded) {
      fetch();
    }
    setLoaded(false);
  }, [debouncedTerm]);

  useEffect(() => {
    if (!freeze && !loaded) {
      setLoading(true);
    }
  }, [term]);

  useEffect(() => {
    if (value === null) {
      setTerm("");
    }
  }, [value]);

  useEffect(() => {
    if (value && !term) {
      const mappedValues = mapDataValue(value);
      setTerm(mappedValues?.label || "");
    }
  }, [value]);

  const nullOrUndefinedValue = value === null ? null : undefined;
  return (
    <Autocomplete
      disabled={disabled}
      forcePopupIcon={false}
      ListboxProps={{
        style: {
          maxHeight: "10rem",
        },
      }}
      filterOptions={filterOptions}
      noOptionsText={
        freeSolo ? "No matching values found, current value will be created" : "Please enter or adjust the search term"
      }
      onFocus={() => {
        if (!freeze && options.length === 0 && !fetchInProgress.current) {
          fetch();
        }
      }}
      onChange={(event, newValue): void => {
        if (typeof newValue === "object") {
          setTerm(newValue?.label || "");
          onChange(event, newValue?.data || null);
        }
      }}
      onInputChange={(event, termValue): void => {
        if (event && event.type === "change") {
          let newValue: T | null = null;
          if (term.startsWith(termValue)) {
            setFreeze(false);
          }
          if (freeSolo && mapToData) {
            newValue = mapToData(termValue);
          }
          onChange(event, newValue);
          setTerm(termValue);
        }
      }}
      open={open}
      openOnFocus={openOnFocus}
      inputValue={term}
      loading={loading}
      value={value ? mapDataValue(value) : nullOrUndefinedValue}
      loadingText="Loading..."
      options={loading ? [] : options}
      getOptionLabel={(option): string => option?.label || ""}
      getOptionSelected={(option, v): boolean => option?.key === v?.key}
      renderInput={(params): ReactElement => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          className={error ? classes.error : ""}
          error={error}
          fullWidth
          helperText={helperText}
          label={mandatory ? `${inputLabel}*` : inputLabel}
          variant="outlined"
          margin="normal"
          {...TextFieldProps}
        />
      )}
      renderOption={(option, { inputValue }): ReactElement => {
        const matches = match(option.label, inputValue);
        const parts = parse(option.label, matches);
        return (
          <div>
            {parts.map((part: { text: string; highlight: boolean }) => (
              <span key={uuid()} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                {part.text}
              </span>
            ))}
          </div>
        );
      }}
    />
  );
}

export default forwardRef(CustomSearchAutocomplete);
